/// <amd-module name="Wit/QueriesHub/ResultsView/QueryResultCSVImporter" />
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { tryConvertStringToDate } from "Wit/Common/DateUtils";
import { CoreFieldRefNames, DalFields, FieldType } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { getCurrentCultureFromLegacyContext } from "Wit/Common/Utilities/FormatUtils";
import { IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import { IWorkItemImportIdentityService, WorkItemIdentityServiceName } from "Wit/Common/WorkItemIdentityService/IWorkItemIdentityService";
import { IWorkItemNodeService } from "Wit/Common/WorkItemNodeService/IWorkItemNodeService";
import { IWorkItemTypesService } from "Wit/Common/WorkItemTypesService/IWorkItemTypesService";
import * as Resources from "Wit/QueriesHub/Resources";
import { IFieldProjectData, IWorkItemData, IWorkItemTypeData } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItemDataManager } from "Wit/WorkItemDataManager/WorkItemDataManager";
import { LinkTypeName, PageSizes } from "Wit/WorkItemModel/OM/";
import { IFieldSetValueOptions, IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { isEditable } from "Wit/WorkItemModel/WorkItem/FieldUtils";
import { WorkItemLink } from "Wit/WorkItemModel/WorkItem/Links";
import { WorkItem } from "Wit/WorkItemModel/WorkItem/WorkItem";

export interface IWorkItemsImportData {
    columns: string[];
    fieldColumns: IFieldEntry[];
    idsMap: {
        [id: string]: number; // id -> index in fieldValues
    };
    fieldValues: string[][];
    targetIds: number[];
    sourceIds: number[];
    isTreeQuery: boolean;
    workItems: IWorkItem[];
}

interface IWorkItemFieldReference {
    fieldReferenceName: string;
    workItem: IWorkItem;
}

export class QueryResultCSVImporter {
    public static updateCSVImportData(importData: IWorkItemsImportData, tempId: number, newId: number): void {
        if (tempId >= 0) {
            return;
        }
        importData.idsMap[newId] = importData.idsMap[tempId];
        delete importData.idsMap[tempId];

        const targetIds: number[] = importData.targetIds;
        const sourceIds: number[] = importData.sourceIds;

        for (let i = 0; i < sourceIds.length; i++) {
            if (sourceIds[i] === tempId) {
                sourceIds[i] = newId;
            }
            if (targetIds[i] === tempId) {
                targetIds[i] = newId;
            }
        }
    }
    // bareHeader means a header that has been stripped of its number and lowercased
    private _requiredHeadersMap: { [bareHeader: string]: string };
    private _requiredHeaders: string[] = [];
    private _idHeader: string;
    private _workItemTypeHeader: string;

    private c_lineFeedChar = "\n";
    private c_carriageReturnChar = "\r";
    private c_maxWorkItemnsAllowed = 1000;

    private forbiddenWorkItemTypes = [
        "Microsoft.VSTS.WorkItemTypes.TestSuite",
        "Microsoft.VSTS.WorkItemTypes.TestCase",
        "Microsoft.VSTS.WorkItemTypes.TestPlan",
        "Microsoft.VSTS.WorkItemTypes.CodeReviewRequest",
        "Microsoft.VSTS.WorkItemTypes.CodeReviewResponse",
        "Microsoft.VSTS.WorkItemTypes.FeedbackRequest",
        "Microsoft.VSTS.WorkItemTypes.FeedbackResponse",
        "Microsoft.VSTS.WorkItemTypes.SharedStep",
        "Microsoft.VSTS.WorkItemTypes.SharedParameter"
    ];

    private _percentComplete = 0;
    private _lastUpdatePercentComplete = 0;
    private _disposed = false;

    private _identityService: IWorkItemImportIdentityService;
    private fields: IFieldEntry[] = [];
    private fieldMapById: { [key: number]: IFieldEntry } = {};
    private fieldMap: { [key: string]: IFieldEntry } = {};
    private _workItemTypeData: IWorkItemTypeData[] = [];
    private _globalConstantSets: { [key: string]: string[] } = {};
    private _areaNodes: WorkItemClassificationNode;
    private _iterationNodes: WorkItemClassificationNode;
    private _fieldProjectData: IFieldProjectData;

    constructor(private _projectId: string, private pageContext: IVssPageContext, private _updatePanelPercentComplete: (number: number) => void) {
        this._requiredHeaders = [CoreFieldRefNames.Title, CoreFieldRefNames.WorkItemType];
        this._requiredHeadersMap = {};
        this._identityService = pageContext.getService<IWorkItemImportIdentityService>(WorkItemIdentityServiceName);
    }

    /*
     * Throws error string if errors have been encountered
     */
    public async importCSVFile(context: IVssPageContext, csv: string | undefined): Promise<IWorkItemsImportData | undefined> {
        if (!csv) {
            return;
        }
        this._resetPercentComplete();

        if (Object.keys(this._requiredHeadersMap).length === 0) {
            await this._populateHeaders();
        }
        if (this._workItemTypeData.length === 0) {
            await this._populateWorkItemTypesMap();
        }

        if (!this._fieldProjectData) {
            await this._populateFieldProjectData();
        }

        const contents: string[][] = this._parseCSVFile(csv);
        this._trimContents(contents);
        this._validateHeaders(contents);
        this._validateRequiredFields(contents);

        const csvImportData = this._getCSVImportData(contents);

        await this._loadWorkItemsAndSetFieldValues(context, csvImportData);
        return csvImportData;
    }

    public dispose(): void {
        this._disposed = true;
    }

    private _populateFieldProjectData = async (): Promise<void> => {
        const itemDataManager = this.pageContext.getService<IWorkItemDataManager>("IWorkItemDataManager");
        this._fieldProjectData = (await itemDataManager.beginGetFieldProjectData(this._projectId)) ?? { projects: [], fields: [] };
    };

    private _populateHeaders = async (): Promise<void> => {
        // Fields not initialized yet
        let fields: IFieldEntry[] | undefined;
        if (isFeatureFlagEnabled(this.pageContext, FeatureFlags.UseNewWorkItemFieldService, false)) {
            const fieldService = this.pageContext.getService<IWorkItemFieldService2>("IWorkItemFieldService2");
            fields = await fieldService.loadFields();
        } else {
            const fieldService = this.pageContext.getService<IWorkItemFieldService>("IWorkItemFieldService");
            fields = await fieldService.getFieldsAsync();
        }
        if (fields) {
            fields.forEach(field => {
                // check to see if the field already exists so that we dont
                // add the field twice to the fields array
                // we use the map for a faster lookup since these data structures should be in sync.
                let alreadyExists = true;
                if (!this.fieldMapById[field.id]) {
                    alreadyExists = false;
                }

                this.fieldMapById[field.id] = field;
                this.fieldMap[field.name.toUpperCase()] = field;
                this.fieldMap[field.referenceName.toUpperCase()] = field;

                if (!alreadyExists) {
                    this.fields.push(field);
                } else {
                    const index = fields!.findIndex(item => item.id === field.id);

                    // this inconsistency is not expected where fields array doesnt have a value that is
                    // there in the fieldMapById but since these are 2 different data structures we need to be safe
                    // when replacing the value.
                    if (index !== -1) {
                        this.fields[index] = field;
                    } else {
                        this.fields.push(field);
                    }
                }
            });
        }
        this._requiredHeaders.forEach(header => {
            const field = this._getFieldDefinition(header);
            if (field) {
                this._requiredHeadersMap[field.name.toLocaleLowerCase()] = field.name;
            }
        });
        this._idHeader = this._getFieldDefinition(CoreFieldRefNames.Id)?.name;
        this._workItemTypeHeader = this._getFieldDefinition(CoreFieldRefNames.WorkItemType)?.name;
    };

    private async _populateWorkItemTypesMap(): Promise<void> {
        const workItemTypesService = this.pageContext.getService<IWorkItemTypesService>("IWorkItemTypesService");
        this._workItemTypeData = await workItemTypesService.getWorkItemTypeData(this._projectId);

        const constantsToGet: Set<string> = new Set<string>();
        this._workItemTypeData.forEach(wit => {
            if (wit.rules.globals) {
                wit.rules.globals.forEach(g => {
                    constantsToGet.add(g);
                });
            }
        });
        const itemDataManager = this.pageContext.getService<IWorkItemDataManager>("IWorkItemDataManager");
        const constantSetsGotten: { [key: string]: string[] } | null = await itemDataManager.beginGetConstantSets(
            Array.from(constantsToGet.values())
        );
        if (constantSetsGotten) {
            this._globalConstantSets = constantSetsGotten;
        }

        const nodeService = this.pageContext.getService<IWorkItemNodeService>("IWorkItemNodeService");
        const areaNodes = await nodeService.getAreaPathNodes(this._projectId);
        const iterationNodes = await nodeService.getIterationNodes(this._projectId);
        if (areaNodes === undefined || iterationNodes === undefined) {
            throw new Error("Unable to find area or iteration nodes for project: " + this._projectId);
        }
        this._areaNodes = areaNodes;
        this._iterationNodes = iterationNodes;
    }

    private _updatePercentComplete(percentOfIncrement: number, increment: number, maxPercentComplete: number): void {
        if (this._disposed) {
            return;
        }

        this._percentComplete = Math.min(this._percentComplete + percentOfIncrement * increment, maxPercentComplete);

        // Avoid updating state more than necessary
        if (this._percentComplete - this._lastUpdatePercentComplete >= 0.01) {
            this._lastUpdatePercentComplete = this._percentComplete;
            this._updatePanelPercentComplete(this._percentComplete);
        }
    }

    private _resetPercentComplete(): void {
        this._percentComplete = 0;
        this._lastUpdatePercentComplete = 0;
        this._updatePanelPercentComplete(0);
    }

    private _parseCSVFile(csv: string): string[][] {
        if (csv.length === 0) {
            throw Resources.ImportCsvEmptyDatasetError;
        }

        // Append on newline because it signals the end of the last row
        if (csv.slice(-1) !== this.c_lineFeedChar) {
            csv += this.c_lineFeedChar;
        }

        const contents = [];
        let currentRow = [];
        let inToken = false;
        let hasQuotes = false;
        let token = "";

        for (let i = 0; i < csv.length; i++) {
            const c = csv[i];
            const isLineFeedEnding = c === this.c_lineFeedChar;
            const isCarriageReturnEnding = c === this.c_carriageReturnChar && i + 1 < csv.length && csv[i + 1] === this.c_lineFeedChar;
            const isLineBreakChar = isLineFeedEnding || isCarriageReturnEnding;

            // Append token
            if ((c === "," && !inToken) || (c === "," && inToken && !hasQuotes)) {
                currentRow.push(token);
                token = "";
                inToken = false;
                hasQuotes = false;
            } else if ((isLineBreakChar && !inToken) || (isLineBreakChar && inToken && !hasQuotes)) {
                // End of the line - append row
                // Flush token, if exists
                if (token !== "") {
                    currentRow.push(token);
                    token = "";
                }

                // Data shape Error: Number of fields in the current row exceeds the number of column headers
                if (contents.length > 0 && contents[0].length < currentRow.length) {
                    throw format(Resources.ImportCsvExtraFieldsFoundError, contents.length + 1, contents[0].length, currentRow.length);
                } else if (contents.length > 0 && contents[0].length > currentRow.length) {
                    // Must pad row because of fewer field values than number of column headers
                    for (let i = currentRow.length; i < contents[0].length; i++) {
                        currentRow.push("");
                    }
                }

                // Skip over LF
                if (isCarriageReturnEnding) {
                    i++;
                }

                contents.push(currentRow);
                currentRow = [];
                inToken = false;
                hasQuotes = false;
            } else if (c === '"' && !inToken) {
                // Start of double-quoted value
                inToken = true;
                hasQuotes = true;
            } else if (c === '"' && inToken && hasQuotes && i + 1 < csv.length && csv[i + 1] === '"') {
                // Quote found in middle of double-quoted value
                token += '"';
                i++; // skip next iteration
            } else if (c === '"' && inToken && hasQuotes && i + 1 < csv.length && csv[i + 1] !== '"') {
                // End of double-quoted value
                inToken = false;
                hasQuotes = false;

                // Syntax Error: Ended CSV value too early
                if (
                    csv[i + 1] !== "," &&
                    csv[i + 1] !== this.c_lineFeedChar &&
                    !(csv[i + 1] === this.c_carriageReturnChar && i + 2 < csv.length && csv[i + 2] === this.c_lineFeedChar)
                ) {
                    throw format(Resources.ImportCsvDoubleQuoteTerminatedEarlyError, contents.length + 1, currentRow.length + 1);
                }
            } else if (c === '"' && inToken && !hasQuotes) {
                // Syntax Error: Random " that is not enclosed in quotes.
                throw format(Resources.ImportCsvDoubleQuoteNotEnclosedError, contents.length + 1, currentRow.length + 1);
            } else {
                // Otherwise, append character
                inToken = true;
                token += c;
            }
        }

        // Syntax Error: Enclosing value in quotes did not terminate.
        if (hasQuotes) {
            throw format(Resources.ImportCsvNoTerminatingDoubleQuotesError, contents.length + 1, currentRow.length + 1);
        }

        if (contents.length > this.c_maxWorkItemnsAllowed + 1) {
            throw format(Resources.ImportCsvMaxRowsExceededError, this.c_maxWorkItemnsAllowed);
        }

        return contents;
    }

    // Handle edge case where CSV can have empty rows / column on the end of the file.
    // Remove the extra rows/columns to streamline validation & reading the contents
    private _trimContents(contents: string[][]): void {
        // trim rows
        let danglingRowIndex = -1;
        for (let i = contents.length - 1; i > 0 && this._isRowEmpty(contents[i]); i--) {
            danglingRowIndex = i;
        }
        if (danglingRowIndex > 0) {
            contents.splice(danglingRowIndex);
        }

        // trim columns
        let danglingColIndex = -1;
        for (let i = contents[0].length - 1; i > 0 && this._isColumnEmpty(i, contents); i--) {
            danglingColIndex = i;
        }
        if (danglingColIndex > 0) {
            contents.forEach(row => row.splice(danglingColIndex));
        }
    }

    private _validateHeaders(contents: string[][]): void {
        if (contents.length === 0) {
            throw Resources.ImportEmptyDataSet;
        }

        const errorMessages = [];

        // Remove leading and trailing white spaces for column headers
        const headers = (contents[0] = contents[0].map(header => header.trim()));
        const requiredIndicesMap = this._getIndicesMap(headers, this._requiredHeadersMap);

        let emptyColumnHeaderIndices = this._getEmptyColumnHeaderIndices(headers);
        if (emptyColumnHeaderIndices.length > 0) {
            // If an entire column is empty, display separately for clarity
            const emptyColumnIndices = emptyColumnHeaderIndices.filter(index => this._isColumnEmpty(index, contents));
            emptyColumnHeaderIndices = emptyColumnHeaderIndices.filter(index => !this._isColumnEmpty(index, contents));

            if (emptyColumnHeaderIndices.length > 0) {
                errorMessages.push(format(Resources.ImportEmptyColumnHeaders, emptyColumnHeaderIndices.map(index => index + 1).join(", ")));
            }
            if (emptyColumnIndices.length > 0) {
                errorMessages.push(format(Resources.ImportEmptyColumn, emptyColumnIndices.map(index => index + 1).join(", ")));
            }
        }

        const invalidColumnHeaders = this._getInvalidColumnHeaders(headers);
        if (invalidColumnHeaders.length > 0) {
            errorMessages.push(format(Resources.ImportInvalidColumnHeaders, invalidColumnHeaders.join(", ")));
        }

        const missingRequiredHeaders = this._getMissingRequiredHeaders(requiredIndicesMap);
        if (missingRequiredHeaders.length > 0) {
            errorMessages.push(format(Resources.ImportMissingRequiredHeaders, missingRequiredHeaders.join(", ")));
        }

        const duplicateNonRequiredHeaders = this._getDuplicateNonRequiredHeaders(headers);
        if (duplicateNonRequiredHeaders.length > 0) {
            errorMessages.push(format(Resources.ImportDuplicateNonRequiredHeaders, duplicateNonRequiredHeaders.join(", ")));
        }

        // ie. "Title 1, Title 2, Work Item Type 1, Work Item Type 2"
        const duplicateRequiredHeaders = this._getDuplicateRequiredHeaders(requiredIndicesMap);
        if (duplicateRequiredHeaders.length > 0) {
            errorMessages.push(format(Resources.ImportDuplicateRequiredHeaders, duplicateRequiredHeaders.join(", ")));
        }

        // ie. "Title 2, Title 1" OR "Title 1, ID, Title 2"
        const nonConsecutiveTreeHeaderIndices = this._getNonConsecutiveTreeHeaderIndices(requiredIndicesMap, headers);
        if (nonConsecutiveTreeHeaderIndices.length > 0) {
            errorMessages.push(format(Resources.ImportNonConsecutiveTreeHeader, nonConsecutiveTreeHeaderIndices.map(index => index + 1).join(", ")));
        }

        if (errorMessages.length > 0) {
            throw new Error(`${Resources.ImportErrorMessageTitle}\n${errorMessages.join("\n")}`);
        }
    }

    // Returns a dictionary that maps stripped, lowercased required header names within headersMap to their indices within headers
    private _getIndicesMap(headers: string[], headersMap: { [bareHeader: string]: string }): { [bareHeader: string]: number[] } {
        const result: { [key: string]: number[] } = {};
        const importantHeaders = Object.keys(headersMap);
        importantHeaders.forEach(header => {
            result[header] = []; // Array initialization for tree view import compatibility: "Title 1, Title 2, Title 3, ..." => [ i, i+1, i+2, ... ]
        });

        // Indices pushed in ASC order
        headers.forEach((header, index) => {
            header = this._getFieldNameByColumnName(header).toLocaleLowerCase();
            if (!result[header]) {
                return;
            }

            result[header].push(index);
        });

        return result;
    }

    private _getEmptyColumnHeaderIndices(headers: string[]): number[] {
        const result: number[] = [];
        headers.forEach((header, index) => {
            if (header.length === 0) {
                result.push(index);
            }
        });

        return result;
    }

    private _isColumnEmpty(index: number, contents: string[][]): boolean {
        for (let i = 0; i < contents.length; i++) {
            const cell = contents[i][index];
            if (cell && cell.length > 0) {
                return false;
            }
        }
        return true;
    }

    private _isRowEmpty(row: string[]): boolean {
        for (let i = 0; i < row.length; i++) {
            if (row[i] && row[i].length > 0) {
                return false;
            }
        }
        return true;
    }

    private _getFieldDefinition(key: string | number) {
        if (typeof key === "number") {
            return this.fieldMapById[key];
        } else {
            return this.fieldMap[key.toUpperCase()];
        }
    }

    private _getInvalidColumnHeaders(headers: string[]): string[] {
        const result: string[] = [];
        const headersMap: { [key: string]: string } = {};

        // Dedup headers from tree view import
        headers.forEach(header => {
            const key = this._getFieldNameByColumnName(header).toLocaleLowerCase();
            if (key.length > 0 && !headersMap[key]) {
                headersMap[key] = header;
            }
        });

        const distinctHeaders = Object.keys(headersMap);
        distinctHeaders.forEach(header => {
            const field = this._getFieldDefinition(header);
            if (!field) {
                const originalNameStripped = this._getFieldNameByColumnName(headersMap[header]);
                result.push(originalNameStripped); // Push 1st occurrence of original name
            }
        });

        return result;
    }

    // Returns an array of requiredHeaders that are not found in headers
    private _getMissingRequiredHeaders(requiredIndicesMap: { [bareHeader: string]: number[] }): string[] {
        const missingRequiredHeaders = Object.keys(requiredIndicesMap).filter(header => requiredIndicesMap[header].length === 0);

        // Return original header names
        return missingRequiredHeaders.map(header => this._requiredHeadersMap[header]);
    }

    private _getDuplicateNonRequiredHeaders(headers: string[]): string[] {
        const nonRequiredHeadersMap: { [key: string]: string } = {};
        headers.forEach(header => {
            const strippedOriginalName = this._getFieldNameByColumnName(header);
            const key = strippedOriginalName.toLocaleLowerCase();
            if (key.length > 0 && !this._requiredHeadersMap[key]) {
                nonRequiredHeadersMap[key] = strippedOriginalName;
            }
        });

        const nonRequiredIndicesMap = this._getIndicesMap(headers, nonRequiredHeadersMap);
        return this._getDuplicateHeaders(nonRequiredIndicesMap, nonRequiredHeadersMap);
    }

    private _getDuplicateRequiredHeaders(requiredIndicesMap: { [bareHeader: string]: number[] }): string[] {
        const result = this._getDuplicateHeaders(requiredIndicesMap, this._requiredHeadersMap);

        // Return if there are multiple duplicates: BOTH 'Title 1', Title 2' and 'Work Item Type 1', 'Work Item Type 2'
        return result.length > 1 ? result : [];
    }

    private _getDuplicateHeaders(indicesMap: { [bareHeader: string]: number[] }, headersMap: { [bareHeader: string]: string }): string[] {
        const result: string[] = [];
        const headers = Object.keys(indicesMap);

        headers.forEach(header => {
            const indices = indicesMap[header];
            if (indices.length > 1) {
                result.push(headersMap[header]);
            }
        });

        return result;
    }

    private _getNonConsecutiveTreeHeaderIndices(requiredIndicesMap: { [bareHeader: string]: number[] }, headers: string[]): number[] {
        const indicesArr = Object.keys(requiredIndicesMap).map(header => requiredIndicesMap[header]);
        const result: number[] = [];

        for (let i = 0; i < indicesArr.length; i++) {
            const indices = indicesArr[i];

            // Skip singular column headers - we'll let the user use singular 'Title 1' or 'Title 2' and strip the number later
            if (indices.length === 1) {
                continue;
            }

            for (let j = 0; j < indices.length; j++) {
                const index = indices[j];
                const numberSuffix = this._getNumberSuffix(headers[index]);

                // If not consecutive in number or spot, return indices
                if (numberSuffix !== j + 1 || (j > 0 && indices[j] - 1 !== indices[j - 1])) {
                    result.concat(indices.slice()); // Return all instances for now
                }
            }
        }

        return result;
    }

    // Gets a field name by given column name
    // In most cases column name should be same as field name, but in tree view
    // "Export as CSV" exports indented titles as Title 1, Title 2 and etc
    // Hence in such case this function strips the number surfix and returns the real field name
    private _getFieldNameByColumnName(columnName: string): string {
        const field: IFieldEntry = this._getFieldDefinition(columnName);
        if (field) {
            return columnName;
        }

        let result = columnName;
        const parts = columnName.split(" ");

        if (parts.length > 0 && Number(parts[parts.length - 1])) {
            result = parts.slice(0, -1).join(" ");
        }

        return result;
    }

    // Returns the number suffix if it exists or -1 otherwise: "Title 1" -> 1
    private _getNumberSuffix(header: string): number {
        let result = -1;
        const parts = header.split(" ");

        if (parts.length > 0 && Number(parts[parts.length - 1])) {
            result = Number(parts[parts.length - 1]);
        }

        return result;
    }

    // Rely on the fact that occurences of 'Title 1, Title 2, Title 3' signify tree view import
    private _isTreeViewImport(requiredIndicesMap: { [bareHeader: string]: number[] }) {
        const indices = Object.keys(requiredIndicesMap)
            .map(header => requiredIndicesMap[header])
            .filter(indices => indices.length > 1);
        return indices.length > 0;
    }

    // Assumes column headers are validated already
    private _validateRequiredFields(contents: string[][]): void {
        if (contents.length === 0) {
            return;
        }

        const errorMessages = [];
        const headers = contents[0];
        const idIndex = this._getHeaderIndex(headers, this._idHeader);
        const requiredIndicesMap = this._getIndicesMap(headers, this._requiredHeadersMap);
        const idsMap: { [key: string]: number[] } = {};

        let maxIndentationAllowed = 1;

        for (let i = 1; i < contents.length; i++) {
            const csvRow = contents[i].map(field => field.trim());

            // Cache ids and record their row indices
            const id = idIndex >= 0 && csvRow[idIndex].length > 0 ? Number(csvRow[idIndex]) : null;
            if (id && id !== 0) {
                idsMap[id] = idsMap[id] || [];
                idsMap[id].push(i);
            } else if (id && id === 0) {
                errorMessages.push(format(Resources.ImportCsvInvalidWorkItemIdError, this._idHeader, i + 1, idIndex + 1));
            } else if (id !== null && isNaN(id)) {
                errorMessages.push(format(Resources.ImportCsvNonIntegerWorkItemIdError, this._idHeader, i + 1, idIndex + 1));
            }

            // Validate field values that belong under the required headers
            const requiredHeaders = Object.keys(requiredIndicesMap);
            requiredHeaders.forEach(requiredHeader => {
                const indices = requiredIndicesMap[requiredHeader];
                let numFieldsFound = 0;
                let currentIndentation = 0;
                let fieldValue = "";

                // Only one field value must be non-empty
                indices.forEach((index, indentation) => {
                    if (csvRow[index].length > 0) {
                        currentIndentation = indentation;
                        numFieldsFound++;
                        fieldValue = csvRow[index];
                    }
                });

                if (numFieldsFound === 0) {
                    errorMessages.push(format(Resources.ImportCsvEmptyFieldValuesError, i + 1, indices.map(index => index + 1).join(", ")));
                } else if (numFieldsFound > 1) {
                    errorMessages.push(format(Resources.ImportCsvExcessFieldValuesError, i + 1, indices.map(index => index + 1).join(", ")));
                } else if (numFieldsFound === 1 && indices.length > 1) {
                    // Validate for proper tree structure
                    if (currentIndentation > maxIndentationAllowed) {
                        errorMessages.push(format(Resources.ImportCsvIncorrectTreeStructureError, i + 1, indices.map(index => index + 1).join(", ")));
                    } else {
                        maxIndentationAllowed = currentIndentation + 1;
                    }
                } else if (
                    numFieldsFound === 1 &&
                    requiredHeader === this._workItemTypeHeader.toLocaleLowerCase() &&
                    this._workItemTypeData.length > 0
                ) {
                    // Validate for Work Item Type
                    const foundWorkItemType = this._workItemTypeData.find(d => d.name.toLocaleLowerCase() === fieldValue.toLocaleLowerCase());
                    if (foundWorkItemType && this.forbiddenWorkItemTypes.some(wit => wit === foundWorkItemType.referenceName)) {
                        errorMessages.push(
                            format(
                                Resources.ImportCsvForbiddenWorkItemTypeError,
                                fieldValue,
                                this._workItemTypeHeader,
                                i + 1,
                                indices.map(index => index + 1).join(", ")
                            )
                        );
                    }

                    if (!foundWorkItemType) {
                        errorMessages.push(
                            format(
                                Resources.ImportCsvInvalidFieldValueError,
                                fieldValue,
                                this._workItemTypeHeader,
                                i + 1,
                                indices.map(index => index + 1).join(", ")
                            )
                        );
                    }
                }
            });
        }

        // Log duplicate ids
        const ids = Object.keys(idsMap);
        ids.forEach(id => {
            const rows = idsMap[id];
            if (rows.length > 1) {
                errorMessages.push(
                    format(Resources.ImportCsvDuplicateWorkItemIdColumnError, this._idHeader, id, rows.map((index: number) => index + 1).join(", "))
                );
            }
        });

        if (errorMessages.length > 0) {
            throw format(Resources.ImportCsvErrorsFound, errorMessages.join("\n"));
        }
    }

    private _getHeaderIndex(headers: string[], headerName: string): number {
        headerName = headerName.toLocaleLowerCase();
        for (let i = 0; i < headers.length; i++) {
            const header = headers[i].toLocaleLowerCase();
            if (header === headerName) {
                return i;
            }
        }

        return -1;
    }

    // Gets a FieldDefinition by column name.
    // If column name is not a real field name (e.g. Title 1),
    // it strips number suffix and use that to fetch field definition.
    private _getFieldDefinitionByColumnName(columnName: string): IFieldEntry {
        const fieldName = this._getFieldNameByColumnName(columnName);
        return this._getFieldDefinition(fieldName);
    }

    private _getCSVImportData(contents: string[][]): IWorkItemsImportData {
        const result: IWorkItemsImportData = {
            columns: [],
            fieldColumns: [],
            idsMap: {},
            fieldValues: [],
            targetIds: [],
            sourceIds: [],
            isTreeQuery: false,
            workItems: []
        };

        if (contents.length === 0) {
            return result;
        }

        result.columns = contents[0].slice();

        const requiredIndicesMap = this._getIndicesMap(result.columns, this._requiredHeadersMap);
        const treeIndicesArr = Object.keys(requiredIndicesMap)
            .map(header => requiredIndicesMap[header])
            .filter(indices => indices.length > 1);

        result.fieldValues = new Array(contents.length - 1);
        result.sourceIds = new Array(contents.length - 1);
        result.targetIds = new Array(contents.length - 1);
        result.isTreeQuery = this._isTreeViewImport(requiredIndicesMap);

        const treeIndices = treeIndicesArr.length > 0 ? treeIndicesArr[0] : []; // At most 1 instance, sorted in ASC order
        const idIndex = this._getHeaderIndex(result.columns, this._idHeader); // -1 if ID column does not exist
        const indentationMap: { [key: number]: number } = {}; // Indentation -> ID
        let tempId = -1; // Assigned to new work items

        // Remove redundant tree column headers: 'Title 1, Title 2, Title 3' -> 'Title'
        for (let i = treeIndices.length - 1; i >= 0; i--) {
            const index = treeIndices[i];
            if (i > 0) {
                result.columns.splice(index, 1);
            } else {
                result.columns[index] = this._getFieldNameByColumnName(result.columns[index]);
            }
        }

        result.fieldColumns = result.columns.map(column => this._getFieldDefinitionByColumnName(column));
        result.columns = result.fieldColumns.map(field => field.referenceName);

        // Package CSV data into ICSVImportData
        for (let i = 1; i < contents.length; i++) {
            const row: string[] = contents[i].slice().map(fieldValue => fieldValue.trim());
            let indentation = 0;

            // Assign negative ids to new work items
            const id = idIndex >= 0 && row[idIndex].length > 0 ? Number(row[idIndex]) : tempId--;

            // Flatten Tree View CSV: Remove empty fields correspnding to tree view indentation while recording the id's indentation level
            for (let j = treeIndices.length - 1; j >= 0; j--) {
                const index = treeIndices[j];
                if (row[index].length === 0) {
                    row.splice(index, 1);
                } else {
                    indentation = j;
                }
            }

            // Force insert ID column value
            if (idIndex < 0) {
                row.unshift(id.toString());
            } else {
                row[idIndex] = id.toString();
            }

            indentationMap[indentation] = id;

            result.targetIds[i - 1] = id;
            result.idsMap[id] = i - 1;
            result.sourceIds[i - 1] = indentation === 0 ? 0 : indentationMap[indentation - 1];
            result.fieldValues[i - 1] = row;
        }

        // Force insert ID column, if it's not present, because it doesn't make sense not to show the new work item IDs
        if (idIndex < 0) {
            result.columns.unshift(CoreFieldRefNames.Id);
            result.fieldColumns.unshift(this._getFieldDefinitionByColumnName(CoreFieldRefNames.Id));
        }

        return result;
    }

    // Asynchronously load work items
    private async _loadWorkItemsAndSetFieldValues(context: IVssPageContext, importData: IWorkItemsImportData): Promise<void> {
        const ids = importData.targetIds;
        const columns = importData.columns;
        const fieldValuesArr = importData.fieldValues;
        const idsMap = importData.idsMap;
        const sourceIds = importData.sourceIds;

        const typeIndex = columns.indexOf(CoreFieldRefNames.WorkItemType);
        const unsolvedIdentities: { [key: string]: IWorkItemFieldReference[] } = {};

        const separateNewAndExistingWorkItems = (currentIds: number[], newIds: number[], existingIds: number[], workItemTypeNames: string[]) => {
            for (let i = 0; i < currentIds.length; i++) {
                const id = currentIds[i];
                if (id < 0) {
                    const fieldValues = fieldValuesArr[idsMap[id]];
                    workItemTypeNames.push(fieldValues[typeIndex]);
                }

                const idsArr = id > 0 ? existingIds : newIds;
                idsArr.push(id);
            }
        };

        const getExistingWorkItems = async (existingIds: number[]): Promise<IWorkItem[] | undefined> => {
            const itemDataManager = this.pageContext.getService<IWorkItemDataManager>("IWorkItemDataManager");
            try {
                const serverWorkItems = await itemDataManager.beginGetWorkItemData(existingIds);

                const workItems = serverWorkItems
                    ?.filter(workItem => {
                        const workItemTypeData = this._workItemTypeData.find(type => {
                            return type.name === workItem.fields[DalFields.WorkItemTypeId];
                        });
                        return !!workItemTypeData;
                    })
                    ?.map((workItem, index) => {
                        const workItemTypeData = this._workItemTypeData.find(type => {
                            return type.name === workItem.fields[DalFields.WorkItemTypeId];
                        })!;

                        const item = new WorkItem(
                            this.pageContext,
                            workItemTypeData,
                            this._fieldProjectData,
                            {
                                areaPathNodes: this._areaNodes,
                                iterationPathNodes: this._iterationNodes
                            },
                            this._globalConstantSets,
                            workItem
                        );

                        return item;
                    });

                return workItems;
            } catch (error) {
                const errorString =
                    existingIds.length > 1
                        ? format(Resources.ImportCSVWorkItemsNotFoundError, existingIds.join(", "))
                        : format(Resources.ImportCSVWorkItemNotFoundError, existingIds[0]);
                throw format(Resources.ImportCsvErrorsFound, errorString);
            }
        };

        const createNewWorkItems = (_newIds: number[], _workItemTypeNames: string[]): IWorkItem[] => {
            const workItems: IWorkItem[] = [];
            for (let i = 0; i < _workItemTypeNames.length; i++) {
                const id = _newIds[i];
                const typeName: string = _workItemTypeNames[i];
                const typeData = this._workItemTypeData.find(type => type.name.toLocaleLowerCase() === typeName.toLocaleLowerCase());
                const fieldValues = fieldValuesArr[idsMap[id]];
                const workItemData: IWorkItemData = {
                    projectId: this._projectId,
                    fields: {},
                    revisions: null,
                    tags: [],
                    files: [],
                    referencedPersons: {},
                    tempId: id, // This is fine as long we're overwriting negative ids for all new work items with our own import tempIds numbering system
                    loadTime: new Date(),
                    currentExtensions: [],
                    referencedNodes: { areaNodes: [], iterationNodes: [] }
                };

                if (!typeData) {
                    break;
                }

                const workItem = new WorkItem(
                    this.pageContext,
                    typeData,
                    this._fieldProjectData,
                    {
                        areaPathNodes: this._areaNodes,
                        iterationPathNodes: this._iterationNodes
                    },
                    this._globalConstantSets,
                    workItemData
                );
                applyFieldValueChanges(workItem, fieldValues, false);
                workItems.push(workItem);
            }

            return workItems;
        };

        const applyFieldValueChanges = (workItem: IWorkItem, fieldValues: string[], existingWorkItem: boolean = false): void => {
            // Change field values
            const fieldIds: number[] = [];
            const currentCulture = getCurrentCultureFromLegacyContext();

            const isDateParsingFixEnabled = !isFeatureFlagEnabled(this.pageContext, FeatureFlags.DateParsingFixDisabled, false);

            if (fieldValues && fieldValues.length > 0) {
                fieldValues.forEach((fieldValue, index) => {
                    const fieldReferenceName = columns[index].toUpperCase();
                    const field = workItem.getFieldByName(fieldReferenceName);
                    if (field) {
                        fieldIds.push(field.fieldDefinition.id);
                        if (field.fieldDefinition.isIdentity) {
                            const existingName = workItem.getFieldValueByName(fieldReferenceName) ?? "";
                            if (existingName !== fieldValue) {
                                // set the imported displayName first
                                const options: IFieldSetValueOptions = {
                                    fireIdentityEagerValidation: true
                                };
                                workItem.setFieldValueByName(fieldReferenceName, fieldValue, options);

                                // collect unsolved identities with associated fields
                                const key = fieldValue.toLocaleLowerCase();
                                if (fieldValue !== "") {
                                    if (!unsolvedIdentities[key]) {
                                        unsolvedIdentities[key] = [{ fieldReferenceName, workItem }];
                                    } else {
                                        unsolvedIdentities[key].push({ fieldReferenceName, workItem });
                                    }
                                }
                            }
                        } else if (field.fieldDefinition.referenceName === CoreFieldRefNames.WorkItemType) {
                            workItem.setFieldValueByName(fieldReferenceName, fieldValue);
                        } else {
                            // Check if the field is "read only"
                            if (!isEditable(field)) {
                                const existingValue = workItem.getFieldValueByName(fieldReferenceName);
                                if (existingValue != fieldValue && existingWorkItem) {
                                    // Readonly field's value updated, throw error and bail out
                                    throw format(Resources.ImportCsvReadonlyFieldModifiedError, field.fieldDefinition.name);
                                }
                            }
                            if (
                                field.fieldDefinition.referenceName === CoreFieldRefNames.Description &&
                                isFeatureFlagEnabled(context, FeatureFlags.SkipInvalidLinksEnabled, false)
                            ) {
                                const element = document.createElement("div");
                                element.innerHTML = fieldValue;
                                const allMentionAnchors = element.querySelectorAll("a[data-vss-mention]");
                                if (allMentionAnchors?.length) {
                                    fieldValue = fieldValue.replace(/data-vss-mention=\"version:[0-9]+\.[0-9]+\"(?=\>#[0-9]+\<\/a\>)/g, "");
                                }
                            }

                            if (field.fieldDefinition.type === FieldType.DateTime) {
                                const dateValue = tryConvertStringToDate(fieldValue, { currentCulture, format: isDateParsingFixEnabled ? undefined : "g" });
                                if (dateValue) {
                                    if (isDateParsingFixEnabled) {
                                        workItem.setFieldValueByName(fieldReferenceName, dateValue);
                                        return;
                                    }
                                    fieldValue = dateValue.toISOString();
                                }
                            }

                            workItem.setFieldValueByName(fieldReferenceName, fieldValue);
                        }
                    }
                });
            }

            workItem.evaluate(fieldIds, fieldIds);

            this._updatePercentComplete(1 / ids.length, 1, 1);
        };

        const addLinks = async (ids: number[], workItems: IWorkItem[]): Promise<void> => {
            for (let i = 0; i < ids.length; i++) {
                const sourceId = sourceIds[idsMap[ids[i]]];
                if (sourceId !== 0) {
                    // TODO: get work item
                    const workItem = workItems.find(item => item.id == ids[i] || item.tempId == ids[i]);
                    const parentWorkItem = workItems.find(item => item.id == sourceId || item.tempId == sourceId);
                    // Always create a link that is directed towards an existing work item, unless both work items are new, then we always create a 'Parent' link
                    if (workItem) {
                        if (sourceId < 0 && workItem.id > 0 && parentWorkItem) {
                            parentWorkItem.addLinks([await WorkItemLink.create(context, LinkTypeName.Child_Link_Name, workItem.id)]);
                        } else {
                            workItem.addLinks([await WorkItemLink.create(context, LinkTypeName.Parent_Link_Name, sourceId)]);
                        }
                    }
                }
            }
        };

        const newIds: number[] = [];
        const existingIds: number[] = [];
        const workItemTypeNames: string[] = [];
        const workItemModels: IWorkItem[] = [];

        // Separate the work items upfront
        separateNewAndExistingWorkItems(ids, newIds, existingIds, workItemTypeNames);

        // Asynchronously fetch 200 WI from the server
        for (let i = 0; i < existingIds.length; i += PageSizes.QUERY) {
            if (this._disposed || !this._workItemTypeData) {
                return;
            }
            const subsetIds = existingIds.slice(i, i + PageSizes.QUERY);
            const workItems = await getExistingWorkItems(subsetIds);
            if (workItems) {
                for (let workItemIndex = 0; workItemIndex < workItems.length; workItemIndex++) {
                    try {
                        const fieldValuesArrIndex = idsMap[subsetIds[workItemIndex]];
                        const fieldValues = fieldValuesArr[fieldValuesArrIndex];
                        applyFieldValueChanges(workItems[workItemIndex], fieldValues, true);
                    } catch (error) {
                        throw format(Resources.ImportCsvErrorsFound, error);
                    }
                }

                workItemModels.push(...workItems);
            }
        }

        workItemModels.push(...createNewWorkItems(newIds, workItemTypeNames));

        await this._autoResolveIdentityFields(unsolvedIdentities);

        if (this._disposed) {
            return;
        }

        await addLinks(ids, workItemModels);
        importData.workItems = workItemModels;
    }

    private async _autoResolveIdentityFields(unresolvedIdentityMap: { [key: string]: IWorkItemFieldReference[] }): Promise<void> {
        const keys = Object.keys(unresolvedIdentityMap);
        if (keys.length == 0) {
            return Promise.resolve();
        }

        const IdentityPageSize = 50; // batch size used for getIdentities call
        const allPromises = [];

        for (let i = 0; i < keys.length; i += IdentityPageSize) {
            const identityNames = keys.slice(i, i + IdentityPageSize);
            allPromises.push(this._beginResolveIdentityFields(unresolvedIdentityMap, identityNames));
        }

        await Promise.all(allPromises);

        return Promise.resolve();
    }

    private _beginResolveIdentityFields = async (
        unresolvedIdentityMap: { [key: string]: IWorkItemFieldReference[] },
        keys: string[]
    ): Promise<void> => {
        const allIdentities = keys.join(";");
        const resolvedIdentityMap: { [key: string]: string } = {};
        try {
            await this._identityService.getIdentityMruAsync(); //init MRU cache
            const identities = await this._identityService.searchIdentitiesAsync(allIdentities);
            if (identities?.length) {
                identities.forEach(identity => {
                    let queryToken = identity.queryToken?.toLocaleLowerCase();
                    if (queryToken && identity.displayName && !resolvedIdentityMap[queryToken]) {
                        resolvedIdentityMap[queryToken] = identity.displayName;
                    }
                });
            }
        } catch {
            return Promise.reject(Resources.ImportCSVErrorHeader);
        }
        Object.keys(resolvedIdentityMap).forEach(identity => {
            // set value to resolved identity name for fields associated with the imported identity value
            unresolvedIdentityMap[identity].forEach(workItemFieldRef => {
                workItemFieldRef.workItem.setFieldValueByName(workItemFieldRef.fieldReferenceName, resolvedIdentityMap[identity]);
            });
        });
    };
}
